/**
 * Secure Proxy Whitelist Dialog
 *
 * Staff-only editor for `allowed_domains` and `allowed_ips` (CIDR) on a
 * `secure_proxy` use case. Self-contained dialog launched from the
 * Organization Settings page (Plan 05 will wire the launcher). No new route,
 * no sidebar entry, no feature flag — same code in every environment.
 *
 * Per Phase-2 decisions:
 *   - D-01: dialog shape, not a standalone page.
 *   - D-02: plain axios via erp-integration-client (no internal SDK).
 *   - D-04: two-click Save with an inline diff panel (NOT a nested dialog).
 *   - D-06: no stage conditionals, no feature flags.
 *
 * Inline validation uses the same error strings as the backend (see
 * `erp-integration-validators.ts`), so an inline "client-side" error and a
 * 400 from the server look identical to staff.
 */
import { useEffect, useMemo, useState } from 'react'

import {
  validateCidr,
  validateDomainPattern
} from '@/api/erp-integration-validators'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { MultiSelectAutoComplete } from '@/components/ui/multi-select-autocomplete'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { SecureProxyWhitelistHistory } from '@/components/secure-proxy-whitelist-history'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from '@/components/ui/use-toast'
import {
  useIntegrationsByOrg,
  useMutationUpdateSecureProxyWhitelist,
  useSecureProxyUseCases,
  useSecureProxyWhitelist
} from '@/services/erp-integration.service'

export interface SecureProxyWhitelistDialogProps {
  orgId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

type DialogMode = 'editing' | 'confirming'

type PerItemErrors = Record<string, string>

interface WhitelistDiff {
  addedDomains: string[]
  removedDomains: string[]
  addedIps: string[]
  removedIps: string[]
}

interface PerItemValidationError {
  item: string
  message: string
}

// --- helpers ---------------------------------------------------------------

const diffLists = (
  before: readonly string[],
  after: readonly string[]
): { added: string[]; removed: string[] } => {
  const beforeSet = new Set(before)
  const afterSet = new Set(after)
  const added = after.filter((value) => !beforeSet.has(value))
  const removed = before.filter((value) => !afterSet.has(value))

  return { added, removed }
}

const hasStringData = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0

const extractServerMessage = (error: unknown): string => {
  if (!error || typeof error !== 'object') {
    return 'Unknown error'
  }

  const candidate = error as {
    response?: { status?: number; data?: unknown }
    message?: string
  }
  const data = candidate.response?.data

  if (data && typeof data === 'object') {
    const maybeMessage = (data as { message?: unknown; error?: unknown })
      .message
    const maybeError = (data as { message?: unknown; error?: unknown }).error

    if (hasStringData(maybeMessage)) {
      return maybeMessage
    }

    if (hasStringData(maybeError)) {
      return maybeError
    }
  }

  if (hasStringData(candidate.message)) {
    return candidate.message
  }

  return 'Unknown error'
}

const getHttpStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined
  }

  const status = (error as { response?: { status?: number } }).response?.status

  return typeof status === 'number' ? status : undefined
}

const mapPerItemServerError = (
  message: string,
  candidates: readonly string[]
): string | null => {
  // Best-effort: match each whitelist item against the server message. The
  // backend error strings include the offending item in quotes (see
  // erp-integration-validators.ts) so a substring match is robust.
  for (const candidate of candidates) {
    if (candidate && message.includes(`"${candidate}"`)) {
      return candidate
    }
  }

  for (const candidate of candidates) {
    if (candidate && message.includes(candidate)) {
      return candidate
    }
  }

  return null
}

// --- component -------------------------------------------------------------

export function SecureProxyWhitelistDialog({
  orgId,
  open,
  onOpenChange
}: SecureProxyWhitelistDialogProps): JSX.Element {
  const [integrationId, setIntegrationId] = useState<string | undefined>(
    undefined
  )
  const [useCaseId, setUseCaseId] = useState<string | undefined>(undefined)
  const [draftDomains, setDraftDomains] = useState<string[]>([])
  const [draftIps, setDraftIps] = useState<string[]>([])
  const [mode, setMode] = useState<DialogMode>('editing')
  const [perItemServerErrors, setPerItemServerErrors] = useState<PerItemErrors>(
    {}
  )
  const [hasSeededFromServer, setHasSeededFromServer] = useState(false)

  // Reset local state when the dialog is re-opened or selectors change.
  useEffect(() => {
    if (!open) {
      setIntegrationId(undefined)
      setUseCaseId(undefined)
      setDraftDomains([])
      setDraftIps([])
      setMode('editing')
      setPerItemServerErrors({})
      setHasSeededFromServer(false)
    }
  }, [open])

  useEffect(() => {
    // Selecting a new integration/use-case pair resets the editor to a fresh
    // unseeded state so the next GET can seed it cleanly.
    setDraftDomains([])
    setDraftIps([])
    setMode('editing')
    setPerItemServerErrors({})
    setHasSeededFromServer(false)
  }, [integrationId, useCaseId])

  const integrations = useIntegrationsByOrg({ orgId })
  const useCases = useSecureProxyUseCases({ orgId, integrationId })

  const whitelistQuery = useSecureProxyWhitelist(
    orgId,
    integrationId,
    useCaseId
  )
  const updateMutation = useMutationUpdateSecureProxyWhitelist({
    orgId,
    integrationId: integrationId ?? '',
    useCaseId: useCaseId ?? ''
  })

  const serverSnapshot = whitelistQuery.data

  // Seed the drafts from the server snapshot exactly once per selection.
  useEffect(() => {
    if (!serverSnapshot || hasSeededFromServer) {
      return
    }

    setDraftDomains(serverSnapshot.allowed_domains ?? [])
    setDraftIps(serverSnapshot.allowed_ips ?? [])
    setHasSeededFromServer(true)
  }, [serverSnapshot, hasSeededFromServer])

  // --- validation --------------------------------------------------------

  const domainErrors: PerItemValidationError[] = useMemo(() => {
    const results: PerItemValidationError[] = []

    for (const item of draftDomains) {
      const message = validateDomainPattern(item)

      if (message) {
        results.push({ item, message })
      }
    }

    return results
  }, [draftDomains])

  const ipErrors: PerItemValidationError[] = useMemo(() => {
    const results: PerItemValidationError[] = []

    for (const item of draftIps) {
      const message = validateCidr(item)

      if (message) {
        results.push({ item, message })
      }
    }

    return results
  }, [draftIps])

  const isValid = domainErrors.length === 0 && ipErrors.length === 0

  // --- diff --------------------------------------------------------------

  const diff: WhitelistDiff = useMemo(() => {
    const domains = diffLists(
      serverSnapshot?.allowed_domains ?? [],
      draftDomains
    )
    const ips = diffLists(serverSnapshot?.allowed_ips ?? [], draftIps)

    return {
      addedDomains: domains.added,
      removedDomains: domains.removed,
      addedIps: ips.added,
      removedIps: ips.removed
    }
  }, [serverSnapshot, draftDomains, draftIps])

  const hasChanges =
    diff.addedDomains.length +
      diff.removedDomains.length +
      diff.addedIps.length +
      diff.removedIps.length >
    0

  const isSaving = updateMutation.isPending
  const selectionComplete = !!integrationId && !!useCaseId
  const isLoading = selectionComplete && whitelistQuery.isLoading
  const isError = selectionComplete && whitelistQuery.isError

  const saveDisabled =
    isSaving ||
    isLoading ||
    isError ||
    !isValid ||
    !hasChanges ||
    !selectionComplete ||
    !hasSeededFromServer

  // --- handlers ----------------------------------------------------------

  const clearPerItemErrorFor = (item: string) => {
    if (!(item in perItemServerErrors)) {
      return
    }

    setPerItemServerErrors((current) => {
      const next = { ...current }

      delete next[item]

      return next
    })
  }

  const handleAddDomain = (item: string) => {
    setDraftDomains((current) =>
      current.includes(item) ? current : [...current, item]
    )
    // Any edit resets the confirming state — the diff must be recomputed and
    // re-approved.
    setMode('editing')
  }

  const handleRemoveDomain = (item: string) => {
    setDraftDomains((current) => current.filter((value) => value !== item))
    clearPerItemErrorFor(item)
    setMode('editing')
  }

  const handleAddIp = (item: string) => {
    setDraftIps((current) =>
      current.includes(item) ? current : [...current, item]
    )
    setMode('editing')
  }

  const handleRemoveIp = (item: string) => {
    setDraftIps((current) => current.filter((value) => value !== item))
    clearPerItemErrorFor(item)
    setMode('editing')
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  /**
   * "Restore this state" from the history panel: pre-populate drafts with the
   * historical snapshot, reset server-side errors, force `editing` mode, and
   * toast a reminder. Per D-05 the actual PUT still requires a Save click.
   */
  const handleRestore = (snapshot: {
    allowed_domains: string[]
    allowed_ips: string[]
  }) => {
    setDraftDomains(snapshot.allowed_domains)
    setDraftIps(snapshot.allowed_ips)
    setPerItemServerErrors({})
    setMode('editing')
    toast({
      title: 'Restored — click Save to apply',
      variant: 'default'
    })
  }

  const performSave = () => {
    // Send only the lists that actually changed so we don't write back
    // identical payloads on either field.
    const body: {
      allowed_domains?: string[]
      allowed_ips?: string[]
    } = {}

    if (
      diff.addedDomains.length > 0 ||
      diff.removedDomains.length > 0
    ) {
      body.allowed_domains = draftDomains
    }

    if (diff.addedIps.length > 0 || diff.removedIps.length > 0) {
      body.allowed_ips = draftIps
    }

    updateMutation.mutate(body, {
      onSuccess: () => {
        toast({ title: 'Whitelist updated' })
        setMode('editing')
        setPerItemServerErrors({})
        // Drafts already hold the new state we just persisted — keep them as
        // the post-save snapshot. Do NOT reset `hasSeededFromServer` here:
        // doing so causes the seed effect to re-fire while the invalidated
        // query is still serving cached pre-save data, snapping drafts back
        // to what the user just changed. The query is invalidated by the
        // service hook so the cache is fresh next time the dialog opens.
      },
      onError: (error: unknown) => {
        const status = getHttpStatus(error)
        const message = extractServerMessage(error)

        if (status === 400) {
          const matchedItem = mapPerItemServerError(message, [
            ...draftDomains,
            ...draftIps
          ])

          if (matchedItem) {
            setPerItemServerErrors((current) => ({
              ...current,
              [matchedItem]: message
            }))
          } else {
            toast({
              title: 'Save failed',
              description: message,
              variant: 'destructive'
            })
          }
        } else {
          toast({
            title: 'Save failed',
            description: message,
            variant: 'destructive'
          })
        }

        setMode('editing')
      }
    })
  }

  const handleSaveClick = () => {
    if (mode === 'editing') {
      setMode('confirming')

      return
    }

    performSave()
  }

  // --- render helpers ----------------------------------------------------

  const renderIntegrationSelector = () => {
    // Show a skeleton while the integrations list is loading so staff don't
    // briefly see an input box that then swaps to a dropdown.
    if (integrations.isLoading) {
      return <Skeleton className="h-10 w-full" />
    }

    // Real dropdown whenever the backend returned an array — even if it's
    // empty (empty = "no integrations on this org", not a reason to show a
    // text input that would accept garbage).
    if (!integrations.isError && Array.isArray(integrations.data)) {
      if (integrations.data.length === 0) {
        return (
          <Select disabled value="">
            <SelectTrigger className="disabled:opacity-100 text-placeholder">
              <SelectValue placeholder="No integrations on this organization" />
            </SelectTrigger>
            <SelectContent />
          </Select>
        )
      }

      return (
        <Select
          onValueChange={(value: string) => setIntegrationId(value)}
          value={integrationId ?? ''}
        >
          <SelectTrigger className={integrationId ? 'text-primary' : 'text-placeholder'}>
            <SelectValue placeholder="Select an integration" />
          </SelectTrigger>
          <SelectContent>
            {integrations.data.map((integration) => (
              <SelectItem key={integration.id} value={integration.id}>
                {integration.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )
    }

    // The list endpoint errored. Without the integrations list we can't
    // target a use case or safely PUT an update, so show an error state
    // instead of a raw-id input — there's no recovery path that doesn't
    // involve fetching this list successfully.
    return (
      <div className="rounded-md border border-destructive/60 bg-destructive/10 p-3 space-y-1.5 text-sm">
        <p className="font-medium">Couldn't load integrations</p>
        <p className="text-muted-foreground">
          The erp-integration-api did not return the integrations list for
          this organization. Check your network or retry.
        </p>
        <Button
          className="mt-2"
          onClick={() => integrations.refetch()}
          size="sm"
          variant="outline"
        >
          Retry
        </Button>
      </div>
    )
  }

  const renderUseCaseSelector = () => {
    const disabled = !integrationId

    // Before an integration is picked, the slot is just a waiting disabled
    // Select — don't surface any error even if the shared integrations query
    // has already failed. Staff need to resolve the integrations error first
    // (the integration selector next door shows the Retry).
    if (disabled) {
      return (
        <Select disabled value="">
          <SelectTrigger className="disabled:opacity-100 text-placeholder">
            <SelectValue placeholder="Select an integration first" />
          </SelectTrigger>
          <SelectContent />
        </Select>
      )
    }

    if (useCases.isLoading) {
      return <Skeleton className="h-10 w-full" />
    }

    if (!useCases.isError && Array.isArray(useCases.data)) {
      if (useCases.data.length === 0) {
        return (
          <Select disabled value="">
            <SelectTrigger className="disabled:opacity-100 text-placeholder">
              <SelectValue placeholder="No secure_proxy use cases on this integration" />
            </SelectTrigger>
            <SelectContent />
          </Select>
        )
      }

      return (
        <Select
          onValueChange={(value: string) => setUseCaseId(value)}
          value={useCaseId ?? ''}
        >
          <SelectTrigger className={useCaseId ? 'text-primary' : 'text-placeholder'}>
            <SelectValue placeholder="Select a use case" />
          </SelectTrigger>
          <SelectContent>
            {useCases.data.map((useCase) => (
              <SelectItem key={useCase.id} value={useCase.id}>
                {useCase.display_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )
    }

    // Integration is selected but the underlying query errored. Rare — same
    // cache key as the integrations picker, which is already showing a Retry
    // button. Keep this slot minimal to avoid noise.
    return (
      <Select disabled value="">
        <SelectTrigger className="disabled:opacity-100 text-placeholder">
          <SelectValue placeholder="Couldn't load use cases — retry above" />
        </SelectTrigger>
        <SelectContent />
      </Select>
    )
  }

  const renderItemErrors = (
    clientErrors: readonly PerItemValidationError[],
    relevantItems: readonly string[]
  ): JSX.Element | null => {
    const serverEntries = Object.entries(perItemServerErrors).filter(
      ([item]) => relevantItems.includes(item)
    )

    if (clientErrors.length === 0 && serverEntries.length === 0) {
      return null
    }

    return (
      <ul className="mt-1 space-y-1">
        {clientErrors.map(({ item, message }) => (
          <li
            className="text-destructive text-xs"
            key={`client-${item}`}
          >
            &quot;{item}&quot; — {message}
          </li>
        ))}
        {serverEntries.map(([item, message]) => (
          <li className="text-destructive text-xs" key={`server-${item}`}>
            &quot;{item}&quot; — {message}
          </li>
        ))}
      </ul>
    )
  }

  const renderDiffSection = (
    title: string,
    items: readonly string[],
    prefix: '+' | '−'
  ) => {
    const itemClass =
      prefix === '+'
        ? 'text-green-600 dark:text-green-400'
        : 'text-destructive'

    return (
      <div>
        <p className="font-medium text-xs uppercase text-muted-foreground">
          {title}
        </p>
        {items.length === 0 ? (
          <p className="text-muted-foreground text-sm">—</p>
        ) : (
          <ul className="text-sm">
            {items.map((item) => (
              <li className={itemClass} key={`${prefix}-${item}`}>
                {prefix} {item}
              </li>
            ))}
          </ul>
        )}
      </div>
    )
  }

  // --- render ------------------------------------------------------------

  const saveLabel = mode === 'editing' ? 'Save' : 'Confirm changes'

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-primary">
            Secure Proxy Whitelist
          </DialogTitle>
          <DialogDescription>
            Edit the allowed domains and CIDR ranges for a secure_proxy use
            case on this organization. Changes are audited and apply
            immediately on Confirm.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="integration_id">Integration</Label>
            {renderIntegrationSelector()}
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="use_case_id">Use case</Label>
            {renderUseCaseSelector()}
          </div>
        </div>

        {selectionComplete && isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
        )}

        {selectionComplete && isError && (
          <div className="rounded-md border border-destructive/60 bg-destructive/10 p-3 space-y-1.5 text-sm">
            <p className="font-medium">Couldn't load the current whitelist</p>
            <p className="text-muted-foreground">
              The erp-integration-api returned an error for this use case
              (status {String(
                (whitelistQuery.error as { response?: { status?: number } })
                  ?.response?.status ?? 'unknown'
              )}
              ). Editing is disabled until we can read the existing values.
            </p>
            <Button
              className="mt-2"
              onClick={() => whitelistQuery.refetch()}
              size="sm"
              variant="outline"
            >
              Retry
            </Button>
          </div>
        )}

        {selectionComplete && !isLoading && !isError && (
          <div className="space-y-4">
            <div className="grid gap-1.5">
              <Label>Allowed domains</Label>
              <MultiSelectAutoComplete
                allowNewValues
                items={[]}
                onNewItem={(label) => label}
                onSelect={(item) =>
                  handleAddDomain(
                    typeof item === 'string' ? item : String(item)
                  )
                }
                onUnselect={(item) =>
                  handleRemoveDomain(
                    typeof item === 'string' ? item : String(item)
                  )
                }
                placeholder="Add a domain pattern (e.g., *.example.com)"
                selectedItems={draftDomains}
              />
              {renderItemErrors(domainErrors, draftDomains)}
            </div>

            <div className="grid gap-1.5">
              <Label>Allowed IPs (CIDR)</Label>
              <MultiSelectAutoComplete
                allowNewValues
                items={[]}
                onNewItem={(label) => label}
                onSelect={(item) =>
                  handleAddIp(typeof item === 'string' ? item : String(item))
                }
                onUnselect={(item) =>
                  handleRemoveIp(
                    typeof item === 'string' ? item : String(item)
                  )
                }
                placeholder="Add a CIDR range (e.g., 10.0.0.0/24)"
                selectedItems={draftIps}
              />
              {renderItemErrors(ipErrors, draftIps)}
            </div>
          </div>
        )}

        {mode === 'confirming' && (
          <div className="rounded-md border border-border p-3 text-sm space-y-2">
            <p className="font-medium text-primary">Review changes</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {renderDiffSection(
                'Domains to add',
                diff.addedDomains,
                '+'
              )}
              {renderDiffSection(
                'Domains to remove',
                diff.removedDomains,
                '−'
              )}
              {renderDiffSection('IPs to add', diff.addedIps, '+')}
              {renderDiffSection('IPs to remove', diff.removedIps, '−')}
            </div>
          </div>
        )}

        {selectionComplete && (
          <SecureProxyWhitelistHistory
            integrationId={integrationId}
            onRestore={handleRestore}
            orgId={orgId}
            useCaseId={useCaseId}
          />
        )}

        <DialogFooter className="items-center justify-end flex w-full gap-2">
          <Button
            disabled={isSaving}
            onClick={handleCancel}
            type="button"
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            disabled={saveDisabled}
            loading={isSaving}
            onClick={handleSaveClick}
            type="button"
          >
            {saveLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
